import type { JSX } from 'solid-js';
import { createSignal, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { CreateProjectModal } from '../components/CreateProjectModal';
import { EmptyState } from '../components/EmptyState';
import { LoadMore } from '../components/LoadMore';
import { OverviewStats } from '../components/OverviewStats';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { RepoEnablementPanel } from '../components/RepoEnablementPanel';
import { RepoFilters } from '../components/RepoFilters';
import { ReposKbPanel } from '../components/ReposKbPanel';
import { RepoTable } from '../components/RepoTable';
import { SessionBadge } from '../components/SessionBadge';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Button, buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { useUsage } from '../lib/billing';
import { useKbStatus } from '../lib/kb-status';
import { GH_APP_INSTALL_URL } from '../lib/onboarding';
import { orgHref } from '../lib/org';
import { createRepoFilters } from '../lib/repoFilters';
import { loadedRepos, useRepoGrid } from '../lib/repos';
import { createRepoToggle } from '../lib/repoToggle';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// Repos overview. Container — fetches the repo list + usage rollup, renders the
// stat band, and hands rows to a searchable/filterable table with per-row
// enable/disable (#972). `token` is injectable for tests.
//
// It reads `GET /v1/repos?include=all` (#2540), not the enabled-only default:
// this is the screen where a repo gets turned ON, so it has to be able to show
// one that is currently off. Two kinds are only visible here — a repo that
// predates the App install (discovered dormant; no webhook can announce it) and
// one the maintainer disabled, which used to vanish from every list read.
// Everything else in the dashboard keeps the enabled-only `useRepoList`.
//
// THE KB FRESHNESS TABLE reads its OWN read (`GET /v1/kb/status`), not the
// fleet snapshot: this page is about the account's repos, and the KB rollup is
// one row per enabled repo of this account. It used to ride
// `GET /v1/fleet/utilization`, which is the fleet operator's health surface —
// a page about boxes and keys had no business carrying this table.

export type { RepoRow } from '../lib/repos';

export function Repos(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const me = useMe(token);
  const repos = useRepoGrid(token);
  const usage = useUsage(token);
  // The account's KB freshness, one row per enabled repo (`GET /v1/kb/status`).
  const kbStatus = useKbStatus(token);
  // No token = prod pre-SSO: queries idle → no-session state, not the install CTA.
  const hasSession = token !== null;

  // Create a project (#1727): gated by the SAME `write:repos` mirror the row
  // toggle computes (`toggle.offered()`), never a second copy. The route's own
  // absence (no App credential in the pod ⇒ a plain 404) is deliberately NOT
  // mirrored — the API's refusal renders in the dialog (the scope-gate
  // doctrine, `lib/scope-gate.ts`).
  const [creating, setCreating] = createSignal(false);
  const [choosing, setChoosing] = createSignal(false);
  const filters = createRepoFilters(); // toolbar state + the filter it exists for

  // Per-row enable/disable (#972): in-flight PATCHes keyed by full_name; success
  // reloads the list — on THIS screen a disabled repo stays put as a row you can
  // switch back on, rather than vanishing as it does from the default read;
  // failure → toast.
  const { toast, notify, dismiss } = createToast();
  const toggle = createRepoToggle({ token, me, reload: () => repos.reload(), notify });

  return (
    <>
      <PageHeader
        title={t('repos.title')}
        desc={t('repos.subtitle')}
        actions={
          <>
            {/* The squads that group these repos (#6780, #7010 D12). A plain anchor the
                router's link delegation takes, as `<EmptyState/>`'s CTA is. */}
            <a
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={orgHref('/account/squads')}
            >
              {t('repos.squads_link')}
            </a>
            <Show when={hasSession}>
              <Button
                variant="outline"
                size="sm"
                aria-expanded={choosing() ? 'true' : 'false'}
                onClick={() => setChoosing(!choosing())}
              >
                {t('repo_enablement.open')}
              </Button>
            </Show>
            <Show when={hasSession && toggle.offered()}>
              <Button size="sm" onClick={() => setCreating(true)}>
                {t('repos.create.open')}
              </Button>
            </Show>
            <SessionBadge login={me.data?.github_login ?? null} loading={me.isLoading} />
          </>
        }
      />

      <Show when={hasSession} fallback={<EmptyState message={t('repos.no_session')} />}>
        {/* The stat still counts ENABLED repos — the metric it has always
            reported. A row the bot does not operate is not a connected repo,
            and folding the widened set (#2540) in would inflate it silently.
            `loadedRepos` still owns the loading/error `UNSET` rule; only the count
            it feeds is now a filter rather than a length. */}
        <OverviewStats
          repoCount={loadedRepos(repos)?.filter((r) => r.enabled).length ?? null}
          usage={usage.data}
        />

        <QueryBoundary
          query={repos.view}
          isEmpty={(rows) => rows.length === 0}
          loading={<CardGridSkeleton listClass="grid-cards-2" />}
          empty={
            // The right target mid-funnel AND once complete (#323) — a finished
            // wizard has no install step to land on.
            <EmptyState
              message={t('repos.empty')}
              ctaLabel={t('repos.install_cta')}
              ctaHref={GH_APP_INSTALL_URL}
            />
          }
        >
          {(rows) => (
            <Panel title={t('repos.connected')}>
              <RepoFilters
                query={filters.query()}
                onQuery={filters.setQuery}
                active={filters.active()}
                onToggle={filters.toggleStatus}
                asc={filters.asc()}
                onSort={filters.toggleSort}
              />

              <RepoTable
                rows={filters.visible(rows)}
                isToggling={toggle.isToggling}
                onToggle={toggle.offered() ? toggle.run : undefined}
              />

              {/* Outside the match <Show/>: if `exhaust` hit MAX_AUTO_PAGES the
                  unloaded repos are exactly the ones a "no matches" search is
                  looking for, so the button has to survive an empty result. */}
              <LoadMore list={repos} />
            </Panel>
          )}
        </QueryBoundary>

        {/* Where and how, at any scale (#7802). Mounted on asking: its own paged read, which the
            grid above never needs. */}
        <Show when={choosing()}>
          <RepoEnablementPanel token={token} onChanged={() => repos.reload()} />
        </Show>

        {/* The KB freshness table, under the repos it describes. Its own read,
            its own states: a caller without `read:kb` gets the API's refusal
            (the boundary's denied state), not an empty table that reads like
            "no repo has a knowledge base". */}
        <QueryBoundary query={kbStatus} loading={<Skeleton height="10rem" />}>
          {(body) => <ReposKbPanel repos={body.repos} />}
        </QueryBoundary>
      </Show>

      <CreateProjectModal
        open={creating()}
        token={token}
        onCreated={() => repos.reload()}
        onClose={() => setCreating(false)}
      />

      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </>
  );
}
